import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    TablePagination,
    Checkbox,
    Button,
    CircularProgress,
    Alert,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Sync as SyncIcon,
    HealthAndSafety as HealthNetIcon,
    MoreVert as MoreIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    Person as PatientIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import prescriptionService, { 
    Prescription, 
    PrescriptionFilter, 
    PrescriptionSearchResponse 
} from '../../services/prescriptionService';

interface PrescriptionListProps {
    filters: PrescriptionFilter;
    onFiltersChange: (filters: PrescriptionFilter) => void;
    onSyncComplete?: () => void;
    onDelete?: (prescription: Prescription) => void;
}

const PrescriptionList: React.FC<PrescriptionListProps> = ({
    filters,
    onFiltersChange,
    onSyncComplete,
    onDelete
}) => {
    const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<string[]>([]);
    const [pagination, setPagination] = useState({
        page: 0,
        limit: 50,
        total: 0,
        totalPages: 0
    });

    // Menu states
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);

    const loadPrescriptions = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response: PrescriptionSearchResponse = await prescriptionService.searchPrescriptions({
                ...filters,
                page: filters.page || 1,
                limit: filters.limit || 50
            });

            setPrescriptions(response.data);
            setPagination({
                page: (response.pagination.page - 1), // MUI uses 0-based pages
                limit: response.pagination.limit,
                total: response.pagination.total,
                totalPages: response.pagination.totalPages
            });

        } catch (error: any) {
            console.error('Error loading prescriptions:', error);
            setError('Errore nel caricamento delle prescrizioni: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadPrescriptions();
    }, [loadPrescriptions]);

    const handlePageChange = (event: unknown, newPage: number) => {
        onFiltersChange({
            ...filters,
            page: newPage + 1 // Convert back to 1-based for API
        });
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newLimit = parseInt(event.target.value, 10);
        onFiltersChange({
            ...filters,
            page: 1,
            limit: newLimit
        });
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = prescriptions.map((prescription) => prescription.id);
            setSelected(newSelected);
        } else {
            setSelected([]);
        }
    };

    const handleSelectClick = (event: React.MouseEvent, id: string) => {
        event.stopPropagation();
        const selectedIndex = selected.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        setSelected(newSelected);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, prescription: Prescription) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedPrescription(prescription);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedPrescription(null);
    };

    const handleSyncPrescription = async (prescription: Prescription) => {
        try {
            await prescriptionService.updateSyncStatus(prescription.id, 'pending');
            // Trigger resync
            await loadPrescriptions();
            if (onSyncComplete) {
                onSyncComplete();
            }
        } catch (error: any) {
            setError('Errore nella sincronizzazione: ' + error.message);
        }
    };

    const handleHealthNetSync = async (prescription: Prescription) => {
        try {
            await prescriptionService.syncWithHealthNet([prescription.id]);
            await loadPrescriptions();
            if (onSyncComplete) {
                onSyncComplete();
            }
        } catch (error: any) {
            setError('Errore nella sincronizzazione HealthNET: ' + error.message);
        }
    };

    const getSyncStatusChip = (prescription: Prescription) => {
        switch (prescription.sync_status) {
            case 'synced':
                return (
                    <Chip
                        icon={<SuccessIcon />}
                        label="Sincronizzata"
                        color="success"
                        size="small"
                        variant="outlined"
                    />
                );
            case 'failed':
                return (
                    <Chip
                        icon={<ErrorIcon />}
                        label="Fallita"
                        color="error"
                        size="small"
                        variant="outlined"
                    />
                );
            case 'pending':
            default:
                return (
                    <Chip
                        icon={<PendingIcon />}
                        label="In Attesa"
                        color="warning"
                        size="small"
                        variant="outlined"
                    />
                );
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        try {
            return format(parseISO(dateString), 'dd/MM/yyyy', { locale: it });
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString?: string) => {
        if (!dateString) return '-';
        try {
            return format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: it });
        } catch {
            return dateString;
        }
    };

    const isSelected = (id: string) => selected.indexOf(id) !== -1;

    if (loading && prescriptions.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {/* Bulk Actions */}
            {selected.length > 0 && (
                <Box sx={{ p: 2, backgroundColor: 'action.selected' }}>
                    <Typography variant="subtitle1" component="div">
                        {selected.length} prescrizioni selezionate
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            startIcon={<SyncIcon />}
                            onClick={() => {/* Implement bulk sync */}}
                        >
                            Sincronizza Selezionate
                        </Button>
                        <Button
                            size="small"
                            startIcon={<HealthNetIcon />}
                            onClick={() => {/* Implement bulk HealthNET sync */}}
                        >
                            Sync HealthNET
                        </Button>
                    </Box>
                </Box>
            )}

            <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    color="primary"
                                    indeterminate={selected.length > 0 && selected.length < prescriptions.length}
                                    checked={prescriptions.length > 0 && selected.length === prescriptions.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                            <TableCell>NRE</TableCell>
                            <TableCell>Data Prescrizione</TableCell>
                            <TableCell>Assistito</TableCell>
                            <TableCell>Medico</TableCell>
                            <TableCell>Diagnosi</TableCell>
                            <TableCell>Stato Sync</TableCell>
                            <TableCell>HealthNET</TableCell>
                            <TableCell>Azioni</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prescriptions.map((prescription) => {
                            const isItemSelected = isSelected(prescription.id);
                            
                            return (
                                <TableRow
                                    hover
                                    key={prescription.id}
                                    selected={isItemSelected}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            checked={isItemSelected}
                                            onClick={(event) => handleSelectClick(event, prescription.id)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {prescription.nre}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(prescription.prescription_date)}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PatientIcon fontSize="small" color="action" />
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {prescription.patient_full_name || 
                                                     `${prescription.patient_first_name || ''} ${prescription.patient_last_name || ''}`.trim() ||
                                                     '********'}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {prescription.patient_fiscal_code || 'N/A'}
                                                </Typography>
                                                {prescription.patient_full_name && 
                                                 prescription.patient_full_name !== '********' && (
                                                    <Chip 
                                                        label="Verificato" 
                                                        size="small" 
                                                        color="success" 
                                                        variant="outlined"
                                                        sx={{ height: '16px', fontSize: '10px', mt: 0.5 }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {prescription.doctor_last_name} {prescription.doctor_first_name}
                                        </Typography>
                                        {prescription.doctor_code && (
                                            <Typography variant="caption" color="textSecondary">
                                                {prescription.doctor_code}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                            {prescription.diagnosis_name || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {getSyncStatusChip(prescription)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={prescription.healthnet_synced ? 'Sì' : 'No'}
                                            color={prescription.healthnet_synced ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            onClick={(event) => handleMenuOpen(event, prescription)}
                                        >
                                            <MoreIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={pagination.total}
                rowsPerPage={pagination.limit}
                page={pagination.page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                labelRowsPerPage="Righe per pagina:"
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                }
            />

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    // Implement view details
                    handleMenuClose();
                }}>
                    <ListItemIcon>
                        <ViewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Visualizza Dettagli</ListItemText>
                </MenuItem>
                
                <MenuItem onClick={() => {
                    if (selectedPrescription) {
                        handleSyncPrescription(selectedPrescription);
                    }
                    handleMenuClose();
                }}>
                    <ListItemIcon>
                        <SyncIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Risincronizza</ListItemText>
                </MenuItem>

                <MenuItem 
                    onClick={() => {
                        if (selectedPrescription) {
                            handleHealthNetSync(selectedPrescription);
                        }
                        handleMenuClose();
                    }}
                    disabled={!selectedPrescription?.healthnet_synced}
                >
                    <ListItemIcon>
                        <HealthNetIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Sync HealthNET</ListItemText>
                </MenuItem>
                
                {onDelete && (
                    <MenuItem 
                        onClick={() => {
                            if (selectedPrescription) {
                                onDelete(selectedPrescription);
                            }
                            handleMenuClose();
                        }}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Elimina Prescrizione</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </Paper>
    );
};

export default PrescriptionList;